import type {
  DecideCatalogueSuggestion,
  OwnCatalogueSuggestion,
  OwnCatalogueSuggestions,
} from "@reminder-tracker/backend-contracts";
import { useAction, useMutation } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { decideCatalogueSuggestion, getCatalogueSuggestionQueue } from "../../backend/functions";
import { classifyCatalogueFailure } from "./failure";
import { AdministrationWorkSurface } from "../../ui/administration-surface";

function reference(prefix: "cmd" | "corr") {
  return `${prefix}_${crypto.randomUUID().replaceAll("-", "")}`;
}
function securityError(): Error {
  return Object.assign(new Error("Verify your administrator access before continuing."), {
    data: { code: "REAUTHENTICATION_REQUIRED" },
  });
}
function date(epoch: number) {
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "long", timeZone: "UTC" }).format(epoch);
}

/** Editorial text is explicitly volunteered; this surface never reads private reminders. */
export function CatalogueSuggestionReview({
  onClose,
}: {
  onClose?: () => void;
}): React.JSX.Element {
  const read = useAction(getCatalogueSuggestionQueue);
  const decide = useMutation(decideCatalogueSuggestion);
  const [page, setPage] = useState<OwnCatalogueSuggestions | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [view, setView] = useState<"due" | "accepted">("due");
  const [selected, setSelected] = useState<OwnCatalogueSuggestion | null>(null);
  const [decision, setDecision] = useState<DecideCatalogueSuggestion["decision"]>("deferred");
  const [cadence, setCadence] = useState<DecideCatalogueSuggestion["cadence"]>("monthly");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [securityFailure, setSecurityFailure] = useState<Error | null>(null);
  const [now, setNow] = useState(Date.now);
  const busyRef = useRef(false);
  const pending = useRef<DecideCatalogueSuggestion | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(
    async (nextCursor: string | null) => {
      if (busyRef.current || pending.current !== null) return;
      busyRef.current = true;
      setBusy(true);
      setPage(null);
      setSelected(null);
      try {
        const result = await read({
          view,
          cursor: nextCursor,
          pageSize: 20,
          correlationId: reference("corr"),
        });
        if (mounted.current) {
          setPage(result);
          setCursor(nextCursor);
          setNow(Date.now());
        }
      } catch (error) {
        if (!mounted.current) return;
        if (classifyCatalogueFailure(error).kind === "security")
          setSecurityFailure(securityError());
        else setMessage("The suggestion queue could not be read. Refresh the queue to try again.");
      } finally {
        busyRef.current = false;
        if (mounted.current) setBusy(false);
      }
    },
    [read, view],
  );
  useEffect(() => {
    void load(null);
  }, [load]);
  useEffect(() => {
    const deadlines =
      page?.items
        .map((item) => item.textDeletionDueAtEpochMilliseconds)
        .filter((value) => value > now) ?? [];
    if (deadlines.length === 0) return;
    const timer = setTimeout(
      () => setNow(Date.now()),
      Math.min(2_147_483_647, Math.max(1, Math.min(...deadlines) - Date.now())),
    );
    return () => clearTimeout(timer);
  }, [page, now]);

  const execute = async (command: DecideCatalogueSuggestion) => {
    if (busyRef.current || (pending.current !== null && pending.current !== command)) return;
    pending.current = command;
    busyRef.current = true;
    setBusy(true);
    setMessage(null);
    let succeeded = false;
    try {
      await decide(command);
      pending.current = null;
      setUncertain(false);
      setConfirmed(false);
      setSelected(null);
      setMessage(
        command.decision === "accepted"
          ? "Suggestion accepted for separate catalogue drafting. No preset was published."
          : command.decision === "rejected"
            ? "Suggestion rejected. Its text will be removed within 30 days, or sooner if the submitter removes it."
            : "Suggestion deferred. Its original 180-day limit is unchanged.",
      );
      succeeded = true;
    } catch (error) {
      const failure = classifyCatalogueFailure(error);
      if (failure.kind === "security") setSecurityFailure(securityError());
      if (failure.kind !== "unknown") pending.current = null;
      setUncertain(failure.kind === "unknown");
      setMessage(
        failure.kind === "unknown"
          ? "The decision was not confirmed. Retry the same request before making another decision."
          : "The decision was rejected. Refresh the queue and review the current suggestion before trying again.",
      );
      if (failure.kind !== "unknown") setSelected(null);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
    if (succeeded) await load(cursor);
  };
  if (securityFailure !== null) throw securityFailure;
  const locked = busy || uncertain;
  const visible =
    page?.items.filter(
      (item) => item.proposal !== null && item.textDeletionDueAtEpochMilliseconds > now,
    ) ?? [];
  const proposal =
    selected !== null && selected.textDeletionDueAtEpochMilliseconds > now
      ? selected.proposal
      : null;
  return (
    <section aria-labelledby="suggestion-review-heading">
      <h3 id="suggestion-review-heading">Customer suggestions</h3>
      <p>
        Only deliberately submitted ideas appear here, without submitter identity. Accepting an idea
        does not publish a preset or copy it into the catalogue.
      </p>
      {onClose ? (
        <button type="button" disabled={locked} onClick={onClose}>
          Back to preset versions
        </button>
      ) : null}
      <button
        type="button"
        disabled={locked}
        onClick={() => {
          setMessage(null);
          void load(null);
        }}
      >
        Refresh suggestion queue
      </button>
      <label>
        Suggestion view
        <select
          disabled={locked}
          value={view}
          onChange={(event) => {
            setMessage(null);
            setView(event.target.value as "due" | "accepted");
          }}
        >
          <option value="due">Due for review</option>
          <option value="accepted">Accepted for drafting</option>
        </select>
      </label>
      {busy ? <p role="status">Waiting for confirmation…</p> : null}
      {message ? <p role={uncertain ? "alert" : "status"}>{message}</p> : null}
      {uncertain ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            if (pending.current !== null) void execute(pending.current);
          }}
        >
          Retry same decision
        </button>
      ) : null}
      {selected !== null ? (
        <AdministrationWorkSurface
          id="suggestion-decision"
          title="Review this customer idea"
          purpose="Separate the submitted evidence from the editorial decision."
          state={{
            label:
              proposal === null
                ? "Suggestion text unavailable"
                : selected.decidedAtEpochMilliseconds === null
                  ? "Decision required"
                  : "Accepted for drafting",
            tone:
              proposal === null
                ? "monitor"
                : selected.decidedAtEpochMilliseconds === null
                  ? "action"
                  : "healthy",
          }}
          consequence="Accepting creates permission for separate generic drafting only. It never publishes a preset or copies customer wording into the catalogue."
        >
          {proposal === null ? (
            <p>
              This suggestion's text is no longer available. Refresh the queue after resolving any
              unconfirmed request.
            </p>
          ) : (
            <>
              <h4>Submitted evidence</h4>
              <p>{proposal.purpose}</p>
              <dl>
                <dt>Country</dt>
                <dd>{proposal.marketCode ?? "Not specified"}</dd>
                <dt>Suggested type</dt>
                <dd>{proposal.serviceTypeId ?? "Not specified"}</dd>
                <dt>Text retained until</dt>
                <dd>
                  {date(selected.textDeletionDueAtEpochMilliseconds)} (UTC), unless decided or
                  removed sooner
                </dd>
              </dl>
              {proposal.timing !== null ? (
                <p>
                  Suggested timing ({proposal.timing.anchor}):{" "}
                  {proposal.timing.rules
                    .map((rule) => `${rule.amount} ${rule.unit} before at ${rule.localTime}`)
                    .join("; ")}
                </p>
              ) : (
                <p>No timing suggested.</p>
              )}
            </>
          )}
          {selected.decidedAtEpochMilliseconds !== null ? (
            <p>
              This idea has been accepted. Use it only to prepare separately reviewed, generic
              catalogue wording before its displayed deletion date. It has not been published.
            </p>
          ) : (
            <fieldset disabled={locked || proposal === null}>
              <legend>Editorial decision</legend>
              <label>
                Decision
                <select
                  value={decision}
                  onChange={(event) => {
                    setDecision(event.target.value as DecideCatalogueSuggestion["decision"]);
                    setConfirmed(false);
                  }}
                >
                  <option value="deferred">Defer for another review</option>
                  <option value="accepted">Accept for separate drafting</option>
                  <option value="rejected">Reject</option>
                </select>
              </label>
              {decision === "deferred" ? (
                <label>
                  Next review
                  <select
                    value={cadence}
                    onChange={(event) => {
                      setCadence(event.target.value as DecideCatalogueSuggestion["cadence"]);
                      setConfirmed(false);
                    }}
                  >
                    <option value="monthly">Next monthly review</option>
                    <option value="quarterly">Next quarterly review</option>
                  </select>
                </label>
              ) : (
                <p>
                  Text will be removed within 30 days of this decision, or sooner if the submitter
                  removes it. Acceptance still requires separately reviewed, generic catalogue
                  drafting.
                </p>
              )}
              <label>
                <input
                  type="checkbox"
                  checked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                />
                I have reviewed this idea and the selected decision.
              </label>
              <button
                className="primary-action"
                type="button"
                disabled={!confirmed}
                onClick={() => {
                  void execute({
                    suggestionId: selected.suggestionId,
                    expectedRevision: selected.revision,
                    decision,
                    cadence,
                    commandKey: reference("cmd"),
                    correlationId: reference("corr"),
                    intentCreatedAtEpochMilliseconds: Date.now(),
                  });
                }}
              >
                Confirm suggestion decision
              </button>
            </fieldset>
          )}
          <button
            className="secondary-action"
            type="button"
            disabled={locked}
            onClick={() => {
              setSelected(null);
              setConfirmed(false);
            }}
          >
            Back to suggestion queue
          </button>
        </AdministrationWorkSurface>
      ) : page !== null ? (
        <>
          {visible.length === 0 ? (
            <p>
              {view === "accepted"
                ? "No retained accepted suggestions on this page."
                : "No suggestions due on this page. New suggestions enter the next monthly review; deferred suggestions follow their chosen review date."}
            </p>
          ) : (
            <ul>
              {visible.map((item) => (
                <li key={item.suggestionId}>
                  <p>{item.proposal!.purpose}</p>
                  <button
                    type="button"
                    disabled={locked}
                    onClick={() => {
                      setSelected(item);
                      setConfirmed(false);
                      setDecision("deferred");
                      setCadence("monthly");
                    }}
                  >
                    Review idea
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            type="button"
            disabled={locked || cursor === null}
            onClick={() => {
              void load(null);
            }}
          >
            First suggestion page
          </button>
          <button
            type="button"
            disabled={locked || page.nextCursor === null}
            onClick={() => {
              void load(page.nextCursor);
            }}
          >
            Next suggestion page
          </button>
        </>
      ) : null}
    </section>
  );
}
